import { createRouter, createWebHistory } from 'vue-router';
import LoginView from '../views/LoginView.vue';
import WorkspaceShell from '@/features/hr-workspace/WorkspaceShell.vue';
import store from '@/store';
import { isAnnualAppraisalsEnabled, isJobDescriptionsEnabled } from '@/constants/featureFlags';
import { recoverFromChunkLoadError } from '@/utils/chunkLoadRecovery';
import { getDefaultAuthedRoute } from '@/utils/getDefaultAuthedRoute';
import { lazyRouteView } from '@/features/hr-workspace/constants/shellKeepAlive';
import {
  hasSystemRole,
  isAdminRole,
  isRegularUserRole,
  isSuperAdminRole
} from '@/utils/systemRoles';
import { inboxTabPermissions } from '@/features/hr-workspace/data/shellConfig';

// Route import failures are handled centrally by router.onError, with the target route.
const lazyLoadView = (view, cacheName = view) =>
  lazyRouteView(
    () => import(/* webpackChunkName: "[request]" */ `../views/${view}.vue`),
    cacheName
  );

const lazyShellView = (path, cacheName = path) =>
  lazyRouteView(
    () => import(
      /* webpackChunkName: "workspace-shell-[request]" */ `@/features/hr-workspace/views/${path}.vue`
    ),
    cacheName
  );

const lazyJdView = (path, cacheName = path) =>
  lazyRouteView(
    () => import(
      /* webpackChunkName: "job-descriptions-[request]" */ `@/features/job-descriptions/views/${path}.vue`
    ),
    cacheName
  );

const shellMeta = {
  showNavBar: true,
  showFooter: false,
  showCanvasBG: false,
  requiresAuth: true,
  layout: 'hr-workspace'
};

const tabPlaceholder = (key) => ({
  path: key,
  name: key,
  component: lazyShellView('ModulePlaceholderView'),
  meta: { ...shellMeta, shellTab: key }
});

const routes = [
  {
    path: '/employees/:id/profile-report',
    name: 'employee-profile-report',
    component: lazyLoadView('EmployeeProfileReportView'),
    meta: {
      showNavBar: false,
      showCanvasBG: false,
      requiresAuth: true,
      requiredAnyPermission: ['employees.view.global', 'employees.view.department']
    }
  },
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    meta: { showNavBar: false, showCanvasBG: true, requiresAuth: false }
  },
  {
    path: '/login-alt',
    redirect: '/login'
  },
  {
    path: '/',
    component: WorkspaceShell,
    meta: shellMeta,
    children: [
      {
        path: '',
        redirect: () => getDefaultAuthedRoute(store.state.users?.user)
      },

      // Shell tab routes (top-level paths)
      {
        path: 'tree',
        name: 'tree',
        component: lazyShellView('TreeView'),
        meta: {
          ...shellMeta,
          shellTab: 'tree'
        }
      },
      {
        path: 'charts',
        redirect: (to) => ({ name: 'dashboard', query: to.query, hash: to.hash })
      },
      {
        path: 'employees',
        alias: 'table',
        name: 'employees',
        component: lazyShellView('TableView'),
        meta: {
          ...shellMeta,
          shellTab: 'table',
          title: 'Employees',
          requiredAnyPermission: ['employees.view.global', 'employees.view.department']
        }
      },
      {
        path: 'directory',
        name: 'directory',
        component: lazyShellView('DirectoryView'),
        meta: {
          ...shellMeta,
          shellTab: 'directory',
          requiredAnyPermission: ['employees.view.global', 'employees.view.department']
        }
      },
      {
        path: 'compensation',
        name: 'compensation',
        component: lazyShellView('CompensationView'),
        meta: {
          ...shellMeta,
          shellTab: 'compensation',
          requiredAnyPermission: [
            'compensation.view_all',
            'compensation.view_us',
            'compensation.view'
          ],
          strictPermissions: true
        }
      },
      {
        path: 'attendance',
        name: 'attendance',
        component: lazyShellView('AttendanceView'),
        meta: {
          ...shellMeta,
          shellTab: 'attendance',
          strictPermissions: true,
          requiredAnyPermission: ['attendance.records.view_all', 'attendance.records.view_department', 'attendance.records.view_related']
        }
      },
      {
        path: 'change-log',
        name: 'change-log',
        component: lazyShellView('ChangeLogView'),
        meta: {
          ...shellMeta,
          shellTab: 'change-log',
          title: 'Change Log',
          requiredAnyPermission: ['audit.view_all']
        }
      },
      tabPlaceholder('performance'),
      {
        path: 'past',
        name: 'past',
        component: lazyShellView('PastEmployeesView'),
        meta: {
          ...shellMeta,
          shellTab: 'past',
          requiredAnyPermission: ['employees.view.global', 'employees.view.department']
        }
      },
      tabPlaceholder('kids'),
      {
        path: 'calendar',
        name: 'calendar',
        component: lazyShellView('CalendarView'),
        meta: {
          ...shellMeta,
          shellTab: 'calendar'
        }
      },
      {
        path: 'candidates',
        name: 'candidates',
        component: lazyShellView('CandidatesView'),
        meta: {
          ...shellMeta,
          shellTab: 'candidates',
          requiredAnyPermission: ['candidates.view.global', 'candidates.view_deleted.global']
        }
      },
      {
        path: 'candidates/:id(\\d+)',
        name: 'candidate-detail',
        component: lazyLoadView('CandidateDetailView'),
        meta: {
          ...shellMeta,
          shellTab: 'candidates',
          showWorkspaceToolbar: false,
          requiredAnyPermission: ['candidates.view.global', 'candidates.view_deleted.global']
        }
      },
      {
        path: 'candidates/:id(\\d+)/edit',
        redirect: (to) => ({
          name: 'candidate-detail',
          params: { id: to.params.id },
          query: { ...to.query, edit: '1' }
        })
      },
      {
        path: 'documents',
        name: 'documents',
        component: lazyShellView('DocumentsView'),
        meta: {
          ...shellMeta,
          shellTab: 'documents',
          title: 'Documents',
          requiredAnyPermission: ['documents.catalog.view']
        }
      },
      {
        path: 'job-descriptions',
        name: 'job-descriptions',
        component: lazyJdView('JobDescriptionsView'),
        meta: {
          ...shellMeta,
          shellTab: 'job-descriptions',
          title: 'Job Descriptions',
          requiredAnyPermission: ['jd.templates.view']
        }
      },
      {
        path: 'reports',
        name: 'reports',
        component: lazyShellView('PayrollReportsView'),
        meta: {
          ...shellMeta,
          shellTab: 'reports',
          requiredAnyPermission: ['payroll_reports.view'],
          strictPermissions: true
        }
      },

      // Primary app modules (top nav)
      {
        path: 'dashboard',
        name: 'dashboard',
        component: lazyLoadView('DashboardView'),
        meta: {
          ...shellMeta,
          shellTab: 'dashboard',
          requiredAnyPermission: [
            'dashboard.view.global',
            'dashboard.view.department',
            'employees.view.global',
            'employees.view.department'
          ]
        }
      },
      {
        path: 'plan-coverage',
        name: 'plan-coverage',
        component: lazyLoadView('PlanCoverageView'),
        meta: {
          ...shellMeta,
          requiredAnyPermission: ['dashboard.view.global', 'dashboard.view.department']
        }
      },
      {
        path: 'all-workflows',
        name: 'all-workflows',
        component: lazyLoadView('AllWorkflowsView'),
        meta: {
          ...shellMeta,
          requiredAnyPermission: [
            'dashboard.view.global',
            'dashboard.view.department',
            'employees.view.global',
            'employees.view.department'
          ]
        }
      },
      {
        path: 'inbox',
        name: 'inbox',
        component: lazyLoadView('InboxView'),
        meta: {
          ...shellMeta,
          shellTab: 'inbox',
          visibleForRegularUser: true,
          requiredAnyPermission: inboxTabPermissions
        }
      },
      {
        path: 'account',
        name: 'account',
        component: lazyLoadView('AccountView'),
        meta: shellMeta
      },
      {
        path: 'notifications',
        name: 'notifications',
        component: lazyLoadView('NotificationsView'),
        meta: shellMeta
      },
      {
        path: 'complaints',
        name: 'complaints',
        component: lazyLoadView('ComplaintsView'),
        meta: {
          ...shellMeta,
          shellTab: 'complaints',
          title: 'Complaints and suggestions'
        }
      },
      {
        path: 'employee-reports',
        name: 'employee-reports',
        component: lazyLoadView('EmployeeReportsView'),
        meta: {
          ...shellMeta,
          requiredAnyPermission: ['employee_reports.view.global', 'employee_reports.view.department']
        }
      },
      {
        path: 'employee-reports/:reportId',
        redirect: (to) => ({
          name: 'employee-reports',
          query: { ...to.query, reportId: to.params.reportId },
        }),
      },
      {
        path: 'departments',
        name: 'departments',
        component: lazyLoadView('DepartmentsView'),
        meta: {
          ...shellMeta,
          shellTab: 'departments',
          requiredAnyPermission: ['departments.view.global', 'departments.view.department']
        }
      },
      {
        path: 'positions',
        name: 'positions',
        component: lazyLoadView('PositionsView'),
        meta: {
          ...shellMeta,
          shellTab: 'positions',
          requiredAnyPermission: ['positions.view.global', 'positions.view.department']
        }
      },
      {
        path: 'employees/create',
        name: 'employee-create',
        component: lazyLoadView('EmployeeCreateView'),
        meta: {
          ...shellMeta,
          showWorkspaceToolbar: false,
          requiredAnyPermission: ['employees.create.global', 'employees.create.department']
        }
      },
      {
        path: 'employees/:id(\\d+)/edit',
        name: 'employee-edit',
        component: lazyLoadView('EmployeeEditView'),
        meta: {
          ...shellMeta,
          showWorkspaceToolbar: false,
          requiredAnyPermission: [
            'employees.update.global',
            'employees.update.department',
            'employees_hr.update'
          ]
        }
      },
      {
        path: 'employees/:id(\\d+)',
        name: 'employee-detail',
        component: lazyLoadView('EmployeeDetailView'),
        meta: {
          ...shellMeta,
          requiredAnyPermission: ['employees.view.global', 'employees.view.department']
        }
      },
      {
        path: 'employees/:id(\\d+)/plans/:planId',
        name: 'plan-editor',
        component: lazyLoadView('PlanEditorView'),
        meta: {
          ...shellMeta,
          requiredAnyPermission: ['employees.view.global', 'employees.view.department']
        }
      },
      {
        path: 'employees/:id(\\d+)/jd/new',
        name: 'jd-prepare',
        component: lazyJdView('JdPrepareView'),
        meta: {
          ...shellMeta,
          showWorkspaceToolbar: false,
          title: 'Prepare Job Description',
          requiredAnyPermission: ['jd.generate']
        }
      },
      {
        path: 'employees/:id(\\d+)/jd/:issuedJdId(\\d+)/edit',
        name: 'jd-editor',
        component: lazyJdView('JdEditorView'),
        meta: {
          ...shellMeta,
          showWorkspaceToolbar: false,
          title: 'Job Description',
          requiredAnyPermission: ['jd.view', 'jd.generate']
        }
      },
      {
        path: 'employees/:id(\\d+)/appraisals/:appraisalId',
        name: 'appraisal-editor',
        component: lazyLoadView('AppraisalEditorView'),
        meta: {
          ...shellMeta,
          requiredAnyPermission: ['employees.view.global', 'employees.view.department']
        }
      },
      {
        path: 'my-profile',
        name: 'my-profile',
        component: lazyLoadView('EmployeeDetailView'),
        meta: {
          ...shellMeta,
          selfProfileRoute: true
        }
      },
      {
        path: 'no-access',
        name: 'no-access',
        component: () => import('../views/NoAccessView.vue'),
        meta: shellMeta
      }
    ]
  }
];

const router = createRouter({
  history: createWebHistory('/'),
  routes
});

const getMyProfileRoute = (currentUserId) => {
  if (currentUserId) {
    return { name: 'my-profile' };
  }
  return { name: 'account' };
};

function resolvePermissionDeniedRedirect(permissions, currentUserId) {
  const hasEmployees = permissions.includes('employees.view.global')
    || permissions.includes('employees.view.department');
  const hasDepartments = permissions.includes('departments.view.global')
    || permissions.includes('departments.view.department');
  const hasPositions = permissions.includes('positions.view.global')
    || permissions.includes('positions.view.department');
  const hasCompensation = permissions.includes('compensation.view_all')
    || permissions.includes('compensation.view_us')
    || permissions.includes('compensation.view');
  const hasCandidates = permissions.includes('candidates.view.global')
    || permissions.includes('candidates.view_deleted.global');

  if (hasEmployees) return { name: 'employees' };
  if (hasDepartments) return { name: 'departments' };
  if (hasPositions) return { name: 'positions' };
  if (hasCompensation) return { name: 'compensation' };
  if (hasCandidates) return { name: 'candidates' };
  if (currentUserId) return getMyProfileRoute(currentUserId);
  return { name: 'no-access' };
}

function isSameNamedRoute(target, current) {
  if (!target?.name || target.name !== current.name) return false;
  const targetParams = target.params || {};
  const currentParams = current.params || {};
  const keys = new Set([...Object.keys(targetParams), ...Object.keys(currentParams)]);
  for (const key of keys) {
    if (String(targetParams[key] ?? '') !== String(currentParams[key] ?? '')) {
      return false;
    }
  }
  return true;
}

// Navigation guard — wait for auth bootstrap before permission checks
router.beforeEach(async (to) => {
  if (!store.getters['users/authReady']) {
    await store.dispatch('users/initializeAuth');
  }

  if (!isAnnualAppraisalsEnabled() && to.name === 'appraisal-editor') {
    return { name: 'employees' };
  }

  if (
    !isJobDescriptionsEnabled()
    && (to.name === 'job-descriptions' || to.name === 'jd-editor' || to.name === 'jd-prepare')
  ) {
    return { name: 'employees' };
  }

  const isLogged = store.state.users.user.isLogged;
  const token = localStorage.getItem('srToken');
  const hasLocalToken = !!token && localStorage.getItem('srIsLogged') === 'true';

  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!isLogged && !hasLocalToken) {
      return { name: 'login' };
    }
    const roles = store.state.users?.user?.roles || [];
    const currentUserId = store.state.users?.user?.id;
    const isRegularUser = isRegularUserRole(roles);
    if (to.name === 'my-profile') {
      if (!currentUserId) {
        return { name: 'account' };
      }
      return true;
    }
    if (to.name === 'all-workflows' && !isAdminRole(roles)) {
      store.commit('users/setFlashMessage', 'No access to this page.');
      return getMyProfileRoute(currentUserId);
    }
    if (to.name === 'notifications' && !isAdminRole(roles)) {
      store.commit('users/setFlashMessage', 'No access to this page.');
      return getMyProfileRoute(currentUserId);
    }
    if (to.name === 'employee-reports' && !isAdminRole(roles)) {
      const permissions = store.state.users?.user?.permissions || [];
      if (
        !permissions.includes('employee_reports.view.global') &&
        !permissions.includes('employee_reports.view.department')
      ) {
        store.commit('users/setFlashMessage', 'No access to this page.');
        return getMyProfileRoute(currentUserId);
      }
    }
    if (isRegularUser && to.name === 'employees') {
      return getMyProfileRoute(currentUserId);
    }
    if (isRegularUser && to.name === 'documents') {
      return getMyProfileRoute(currentUserId);
    }
    if (isRegularUser && to.name === 'job-descriptions') {
      return { name: 'my-profile', query: { tab: 'job-description' } };
    }
    const required = to.meta.requiredAnyPermission;
    if (Array.isArray(required) && required.length > 0) {
      const strictPermissions = to.matched.some((record) => record.meta?.strictPermissions);
      // super_admin: same bypass as shellTabVisibility, except salary modules
      if (isSuperAdminRole(roles) && !strictPermissions) {
        return true;
      }
      const permissions = store.state.users?.user?.permissions || [];
      const allowDeptUserWorkflow = !strictPermissions
        && hasSystemRole(roles, 'dept_user')
        && ['plan-editor', 'appraisal-editor'].includes(String(to.name || ''));
      const allowEmployeeSelf = !strictPermissions
        && to.path.startsWith('/employees/')
        && String(to.params?.id ?? '') === String(currentUserId ?? '');
      // Fail closed: empty permissions must not skip requiredAnyPermission.
      // dept_admin must hold explicit permissions (no role path bypass).
      const hasAny = required.some((code) => permissions.includes(code))
        || allowEmployeeSelf
        || allowDeptUserWorkflow;
      if (!hasAny) {
        store.commit('users/setFlashMessage', 'No access to this page.');
        const fallback = resolvePermissionDeniedRedirect(permissions, currentUserId);
        if (isSameNamedRoute(fallback, to)) {
          if (currentUserId && to.name !== 'employee-detail') {
            return getMyProfileRoute(currentUserId);
          }
          return { name: 'no-access' };
        }
        return fallback;
      }
    }
    return true;
  }
  if (to.name === 'login' && (isLogged || hasLocalToken)) {
    return getDefaultAuthedRoute(store.state.users?.user);
  }
  return true;
});

router.onError((error, to) => {
  recoverFromChunkLoadError(error, { target: to?.fullPath });
});

router.afterEach((to) => {
  const pageTitle = to.matched
    .slice()
    .reverse()
    .find((record) => record.meta?.title)?.meta?.title;
  document.title = pageTitle ? `${pageTitle} · HR` : 'HR';

  if (to.meta.showNavBar) {
    store.dispatch('navigation/addToHistory', {
      path: to.path,
      name: to.name,
      meta: to.meta
    });
  }
});

export default router;
